import { useState, useMemo, useEffect } from "react";
import type { PermissionResponseDTO } from "@/shared/dto";
import { Transfer, Table, Flex, Tag, AntTable } from "@/frontend/components/common";
import type {
  TransferProps as CustomTransferProps,
  TransferRenderProps,
  GetProp,
  TableColumnsType,
  TableRowSelection,
} from "@/frontend/components/common";

type TransferItem = GetProp<CustomTransferProps, "dataSource">[number];

interface PermissionTransferItem {
  key: string;
  name: string;
  guardSlug: string;
  nameSection: string | null;
  nameHead: string | null;
}

interface AssignPermissionsProps {
  availablePermissions: PermissionResponseDTO[];
  assignedPermissions: PermissionResponseDTO[];
  onTargetKeysChange?: (targetKeys: string[]) => void;
}

const filterOption = (input: string, item: PermissionTransferItem): boolean => {
  const searchTerm = input.toLowerCase();
  return (
    item.name?.toLowerCase().includes(searchTerm) ||
    item.guardSlug?.toLowerCase().includes(searchTerm) ||
    item.nameSection?.toLowerCase().includes(searchTerm) ||
    item.nameHead?.toLowerCase().includes(searchTerm) ||
    false
  );
};

export function AssignPermissions({ availablePermissions, assignedPermissions, onTargetKeysChange }: AssignPermissionsProps) {
  // Inicializar targetKeys con los permisos ya asignados
  const initialTargetKeys = useMemo(() => {
    return assignedPermissions.map((p) => p.id);
  }, [assignedPermissions]);

  const [targetKeys, setTargetKeys] = useState<CustomTransferProps["targetKeys"]>(initialTargetKeys);

  // Actualizar targetKeys cuando cambien los permisos asignados
  useEffect(() => {
    setTargetKeys(initialTargetKeys);
    if (onTargetKeysChange) {
      onTargetKeysChange(initialTargetKeys as string[]);
    }
  }, [initialTargetKeys, onTargetKeysChange]);

  const onChange: CustomTransferProps["onChange"] = (nextTargetKeys) => {
    setTargetKeys(nextTargetKeys);
    if (onTargetKeysChange) {
      onTargetKeysChange(nextTargetKeys as string[]);
    }
  };

  // Combinar todos los permisos (disponibles + asignados) para el dataSource
  const dataSource = useMemo<PermissionTransferItem[]>(() => {
    const allPermissions = [...availablePermissions, ...assignedPermissions];
    // Eliminar duplicados por ID
    const uniquePermissions = Array.from(
      new Map(allPermissions.map((p) => [p.id, p])).values()
    );
    return uniquePermissions.map((permission) => ({
      key: permission.id,
      name: permission.name,
      guardSlug: permission.guardSlug,
      nameSection: permission.nameSection,
      nameHead: permission.nameHead,
    }));
  }, [availablePermissions, assignedPermissions]);

  const columns: TableColumnsType<PermissionTransferItem> = [
    {
      dataIndex: "name",
      title: "Nombre",
    },
    {
      dataIndex: "guardSlug",
      title: "Guard Slug",
    },
    {
      dataIndex: "nameSection",
      title: "Sección",
      render: (nameSection: string | null) =>
        nameSection ? (
          <Tag style={{ marginInlineEnd: 0 }} color="blue">
            {nameSection}
          </Tag>
        ) : (
          "-"
        ),
    },
    {
      dataIndex: "nameHead",
      title: "Cabeza",
      render: (nameHead: string | null) =>
        nameHead ? (
          <Tag style={{ marginInlineEnd: 0 }} color="cyan">
            {nameHead}
          </Tag>
        ) : (
          "-"
        ),
    },
  ];

  const renderContent = ({
    direction,
    filteredItems,
    onItemSelect,
    onItemSelectAll,
    selectedKeys: listSelectedKeys,
    disabled: listDisabled,
  }: TransferRenderProps) => {
    const tableColumns = direction === "left" ? columns : columns;
    const rowSelection: TableRowSelection<TransferItem> = {
      getCheckboxProps: () => ({ disabled: listDisabled || false }),
      onChange(selectedRowKeys) {
        onItemSelectAll(selectedRowKeys, "replace");
      },
      selectedRowKeys: listSelectedKeys,
      selections: [
        AntTable.SELECTION_ALL,
        AntTable.SELECTION_INVERT,
        AntTable.SELECTION_NONE,
      ],
    };

    return (
      <Table
        rowSelection={rowSelection}
        columns={tableColumns}
        dataSource={filteredItems}
        size="small"
        style={{ pointerEvents: listDisabled ? "none" : undefined }}
        onRow={({ key, disabled: itemDisabled }) => ({
          onClick: () => {
            if (itemDisabled || listDisabled) {
              return;
            }
            onItemSelect(key, !listSelectedKeys.includes(key));
          },
        })}
      />
    );
  };

  return (
    <Flex align="start" gap="middle" vertical>
      <Transfer
        dataSource={dataSource}
        targetKeys={targetKeys}
        showSearch
        showSelectAll={false}
        onChange={onChange}
        filterOption={filterOption}
        renderContent={renderContent}
        titles={["Permisos Disponibles", "Permisos Asignados"]}
        locale={{
          itemUnit: "permiso",
          itemsUnit: "permisos",
          searchPlaceholder: "Buscar permisos",
          notFoundContent: "No se encontraron permisos",
        }}
      />
    </Flex>
  );
}
